import { ReportTestItem } from '@/app/features/reports/utils/entities';
import { TestItem } from '../types/report';
import { Element } from '../types/report';
export interface ProcessedTestData {
  totalTests: number;
  averageScore: number | null;
  medianScore: number | null;
  ratingTypes: string[];
  distinctCounts: {
    assetIds: number;
    testSpecificationIds: number;
    testSuiteIds: number;
    elements: number;
    elementGroups: number;
    elementIds: number;
    elementNames: number;
    buildingIds: number;
    levelIds: number;
    areaIds: number;
  };
  scoreRangeStats: {
    lowestScore: number | null;
    lowestScoreCount: number;
    highestScore: number | null;
    highestScoreCount: number;
  };
  testCounts: {
    testsWithRating: number;
    otherTests: number;
  };
  testDistribution: {
    [key: string]: {
      count: number;
      scaleTypes: {
        [key: string]: number;
      };
    };
  };
}

/**
 * Calculates a unique identifier for an element group based on the first two levels of its path
 * 
 * @param element - The element object containing path and tree_id information
 * @returns A string identifier in the format "level0Id-level1Id" or null if invalid
 * 
 * The function:
 * - Returns null if element or tree_id is missing
 * - Returns null if first path level exists but second doesn't
 * - Combines IDs from first two path levels to create group identifier
 */
function calculateElementGroup(element: Element | null): string | null {
  if (!element || !element.tree_id) return null;

  console.debug('calculateElementGroup', element);

  if (!element.path[0] && element.path[1]) return null;

  return `${element.path[0].id}-${element.path[1].id}`;

}

function calculateMedian(scores: number[]): number | null {
  if (scores.length === 0) return null;
  const sorted = [...scores].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function processTestData(testItemsRaw: ReportTestItem[] = []): ProcessedTestData {
  if (!Array.isArray(testItemsRaw)) {
    console.warn('Invalid input to processTestData: expected an array, got', typeof testItemsRaw);
    return; // Or return a default empty state
  }

  const testItems = testItemsRaw.filter(item => item.exclusionReason === 'included');
  const totalTests = testItems.length;

  if (totalTests === 0) {
    // Return early with a default state if there's no data to process
    return {
      totalTests: 0,
      averageScore: null,
      medianScore: null,
      ratingTypes: [],
      distinctCounts: {
        assetIds: 0,
        testSpecificationIds: 0,
        testSuiteIds: 0,
        elementIds: 0,
        elementNames: 0,
        buildingIds: 0,
        levelIds: 0,
        areaIds: 0,
        elements: 0,
        elementGroups: 0,
      } ,
      scoreRangeStats: {
        lowestScore: null,
        lowestScoreCount: 0,
        highestScore: null,
        highestScoreCount: 0,
      },
      testCounts: {
        testsWithRating: 0,
        otherTests: 0,
      },
      testDistribution: {
        'very_poor': { count: 0, scaleTypes: {} }, 'poor': { count: 0, scaleTypes: {} },
      },
    };
  }

  let scoreSum = 0;
  const validScores: number[] = [];
  const ratingTypes = new Set<string>();
  const testCounts = { testsWithRating: 0, otherTests: 0 };
  const scoreRangeStats = {
    lowestScore: null as number | null,
    highestScore: null as number | null,
    lowestScoreCount: 0,
    highestScoreCount: 0,
  };
  const testDistribution: ProcessedTestData['testDistribution'] = {
    'very_poor': { count: 0, scaleTypes: {} }, 'poor': { count: 0, scaleTypes: {} },
    'fair': { count: 0, scaleTypes: {} }, 'good': { count: 0, scaleTypes: {} },
    'very_good': { count: 0, scaleTypes: {} }, 'no_rating': { count: 0, scaleTypes: {} },
  };
  const distinctSets = {
    assetIds: new Set(), testSpecificationIds: new Set(), testSuiteIds: new Set(),
    elementIds: new Set(), elementNames: new Set(), buildingIds: new Set(),
    levelIds: new Set(), areaIds: new Set(), elements: new Set(), elementGroups: new Set(),
  };

  for (const item of testItems) {
    // Score stats
    if (item.score !== null) {
      validScores.push(item.score);
      scoreSum += item.score;
      if (item.score >= 1 && item.score <= 5) {
        if (scoreRangeStats.lowestScore === null || item.score < scoreRangeStats.lowestScore) {
          scoreRangeStats.lowestScore = item.score;
          scoreRangeStats.lowestScoreCount = 1;
        } else if (item.score === scoreRangeStats.lowestScore) {
          scoreRangeStats.lowestScoreCount++;
        }
        if (scoreRangeStats.highestScore === null || item.score > scoreRangeStats.highestScore) {
          scoreRangeStats.highestScore = item.score;
          scoreRangeStats.highestScoreCount = 1;
        } else if (item.score === scoreRangeStats.highestScore) {
          scoreRangeStats.highestScoreCount++;
        }
      }
    }

    // Rating types
    if (item.ratingType) ratingTypes.add(item.ratingType);

    // Test counts
    item.testSpecification?.type === 'generic' ? testCounts.otherTests++ : testCounts.testsWithRating++;

    // Test distribution
    const rating = item.result || 'no_rating';
    const scaleType = item.ratingType || 'unknown';
    testDistribution[rating].count++;
    testDistribution[rating].scaleTypes[scaleType] = (testDistribution[rating].scaleTypes[scaleType] || 0) + 1;

    // Distinct counts
    distinctSets.assetIds.add(item.assetId);
    distinctSets.testSpecificationIds.add(item.testSpecificationId);
    if (item.testSpecification?.testSuiteId) distinctSets.testSuiteIds.add(item.testSpecification.testSuiteId);
    if (item.element_id) distinctSets.elementIds.add(item.element_id);
    if (item.element_name) distinctSets.elementNames.add(item.element_name);
    if (item.buildingId) distinctSets.buildingIds.add(item.buildingId);
    if (item.levelId) distinctSets.levelIds.add(item.levelId);
    if (item.areaId) distinctSets.areaIds.add(item.areaId);
    if (item.element?.id) distinctSets.elements.add(item.element.id);
    const elementGroup = calculateElementGroup(item.element);
    if (elementGroup) distinctSets.elementGroups.add(elementGroup);
  }

  const averageScore = validScores.length > 0 ? scoreSum / validScores.length : null;
  const medianScore = calculateMedian(validScores);
  const distinctCounts = Object.fromEntries(
    Object.entries(distinctSets).map(([key, set]) => [key, set.size])
  );

  return {
    totalTests,
    averageScore,
    medianScore,
    ratingTypes: Array.from(ratingTypes),
    distinctCounts: distinctCounts as ProcessedTestData['distinctCounts'],
    testCounts,
    testDistribution,
    scoreRangeStats,
  };
}